import { defineStore } from 'pinia';
import Logger from '~/core/logger';

export interface LoadingStore {
    items: Record<string, string[]>;
}

// The context id is in format of `service;metadata-key:metadata-value,...`
function getPrefix(id: string) {
    return id.split(';')[0];
}

let incr = 0;

export const useLoadingStore = defineStore('loading', {
    state: (): LoadingStore => ({
        items: {},
    }),

    getters: {
        isLoading: (state) => (id?: string) => {
            if (!id) return Object.keys(state.items).length > 0;

            const prefix = getPrefix(id);
            if (!state.items[prefix]) return false;

            return prefix === id || state.items[prefix].indexOf(id) !== -1;
        },

        hasAnyLoading: (state) => Object.keys(state.items).length > 0,

        getLoadingItems: (state) => (prefix: string) => {
            return state.items[prefix] || [];
        },
    },

    actions: {
        add(name?: string): () => void {
            const id = name || (incr++).toString();
            Logger.debug('LoadingState', `Added ${id}`);

            const prefix = getPrefix(id);
            if (!this.items[prefix]) {
                this.items[prefix] = [];
            }
            this.items[prefix].push(id);

            // Return cleanup function
            return () => this.remove(id);
        },

        remove(id: string) {
            Logger.debug('LoadingState', `Removed ${id}`);

            const prefix = getPrefix(id);
            if (!this.items[prefix]) return;

            const index = this.items[prefix].indexOf(id);
            if (index === -1) return;

            this.items[prefix].splice(index, 1);
            if (this.items[prefix].length === 0) {
                delete this.items[prefix];
            }
        },

        clear(prefix?: string) {
            if (prefix) {
                delete this.items[prefix];
            } else {
                this.items = {};
            }
        },
    },
});
